import {
  Analytics,
  ApiVersion,
  Avatar,
  AvatarDataset,
  CallInsights,
  CallOverview,
  Conversation,
  Creator,
  CustomerCheckoutModel,
  CustomerCheckoutResponse,
  DeveloperKey,
  DeveloperKeyWithSecret,
  Endpoints,
  Feedback,
  Knowledge,
  Message,
  Method,
  PayAsYouGoModel,
  PayAsYouGoResponse,
  QueryParams,
  ReturnTypes,
  SecondPathParam,
  StripeProducts,
  Video,
  WebhookConfig,
} from "../types"
import { Agent, AgentCapabilityType } from "../types/Agent"
import {
  ExternalApiConfiguration,
  ExternalApiConfigurationPayload,
  ExternalApiConfigurationResponse,
} from "../types/ExternalApiConfiguration"
import { UsageDetails } from "../types/Usage"
import { firebaseAuth, isDev, isLocalDev, isStaging } from "../utils"

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

const backendUrlBaseUrl = isLocalDev
  ? "http://localhost:8000"
  : isDev
    ? "https://api-dev.bey.chat"
    : isStaging
      ? "https://api-staging.bey.chat"
      : "https://api.bey.chat"

const DEFAULT_API_VERSION = ApiVersion.v2

type CallApiOptions = {
  endpoint: Endpoints | string
  method?: Method
  id?: string
  body?: any
  secondPathParam?: SecondPathParam
  returnType?: ReturnTypes
  queryParams?: Record<string, string | number>
  apiVersion?: ApiVersion
}

const buildQueryParams = (queryParams?: QueryParams) => {
  if (!queryParams) return undefined
  const params = new URLSearchParams()
  params.append("limit", queryParams.limit.toString())
  if (queryParams?.cursor && queryParams.cursor !== "") {
    params.append("cursor", queryParams.cursor)
  }
  return Object.fromEntries(params.entries())
}

const callApi = async ({
  endpoint,
  method = Method.GET,
  id,
  body,
  secondPathParam,
  returnType = ReturnTypes.json,
  queryParams,
  apiVersion = DEFAULT_API_VERSION,
}: CallApiOptions) => {
  const idToken = await firebaseAuth?.currentUser?.getIdToken()
  if (!idToken) {
    throw new Error("User is not authenticated")
  }

  const baseUrl = `${backendUrlBaseUrl}/${apiVersion}/`

  const url = new URL(
    secondPathParam
      ? `${baseUrl}${endpoint}/${id}/${secondPathParam}`
      : id
        ? `${baseUrl}${endpoint}/${id}`
        : `${baseUrl}${endpoint}`,
  )

  if (queryParams) {
    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.append(key, value?.toString())
    })
  }

  const response = await fetch(url.toString(), {
    method,
    body: body ? JSON.stringify(body) : undefined,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
  })
  if (!response.ok) {
    const json = await response.json()
    throw new ApiError(
      (json as any).detail || `HTTP Error ${response.status}`,
      response.status,
      json,
    )
  }
  if (returnType === ReturnTypes.blob) {
    return response.blob()
  } else {
    return response.json()
  }
}

const transformDate = (e: any) => {
  return {
    ...e,
    updatedAt: e.updatedAt != null ? new Date(e.updatedAt) : null,
    createdAt: e.createdAt != null ? new Date(e.createdAt) : null,
  }
}

const transformDates = (res: any) => {
  return res
    .map((e) => transformDate(e))
    .sort(
      (a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0),
    )
}

export const listAvatars = async (
  queryParams?: QueryParams,
): Promise<Avatar[]> => {
  const res = await callApi({
    endpoint: Endpoints.avatar,
    queryParams: buildQueryParams(queryParams),
  })
  return transformDates(res.data)
}

export const createAvatar = async (avatar: Avatar) => {
  return transformDate(
    await callApi({
      endpoint: Endpoints.avatar,
      method: Method.POST,
      body: avatar,
    }),
  )
}

export const updateAvatar = async (avatar: Avatar) => {
  return transformDate(
    await callApi({
      endpoint: Endpoints.avatar,
      method: Method.PUT,
      id: avatar.id,
      body: avatar,
    }),
  )
}

export const generateAvatar = async (avatar: Avatar) => {
  return transformDate(
    await callApi({
      endpoint: Endpoints.avatar,
      method: Method.POST,
      id: avatar.id,
      secondPathParam: SecondPathParam.generate,
    }),
  )
}

export const getAvatar = async (avatarId: string): Promise<Avatar> => {
  const avatarFromBe = await callApi({
    endpoint: Endpoints.avatar,
    id: avatarId,
  })
  return transformDate(avatarFromBe)
}

export const deleteAvatar = async (avatar: Avatar) => {
  return callApi({
    endpoint: Endpoints.avatar,
    method: Method.DELETE,
    id: avatar.id,
  })
}

export const createDataset = async (dataset: AvatarDataset) => {
  return callApi({
    endpoint: Endpoints.dataset,
    method: Method.POST,
    body: dataset,
  })
}

export const uploadDataset = async (dataset: AvatarDataset, file: File) => {
  const idToken = await firebaseAuth.currentUser.getIdToken()
  const chunk_size = 1024 * 1024 * 10 // Chunk size set to 10 MB
  let offset = 0
  let chunk_number = 0
  while (offset < file?.size) {
    const chunk = file.slice(offset, offset + chunk_size)

    const chunk_blob = new Blob([chunk], { type: file.type })

    // Create a FormData object to send chunk data
    const formData = new FormData()
    formData.append("file", chunk_blob, file.name)
    formData.append("chunk_number", String(chunk_number))
    formData.append("total_chunks", String(Math.ceil(file?.size / chunk_size)))

    await fetch(
      `${backendUrlBaseUrl}/${DEFAULT_API_VERSION}/${Endpoints.dataset}/${dataset.id}/${SecondPathParam.upload}`,
      {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      },
    )

    offset += chunk_size
    chunk_number += 1
  }

  return
}

export const processDataset = async (dataset: AvatarDataset) => {
  return callApi({
    endpoint: Endpoints.dataset,
    method: Method.POST,
    id: dataset.id,
    secondPathParam: SecondPathParam.process,
  })
}

export const getDataset = async (datasetId: string) => {
  return callApi({ endpoint: Endpoints.dataset, id: datasetId })
}

const loadMediaUrl = async (url: string) => {
  const idToken = await firebaseAuth.currentUser.getIdToken()
  const result = await fetch(url, {
    headers: {
      Authorization: `Bearer ${idToken}`,
    },
  })

  const blob = await result.blob()
  return URL.createObjectURL(blob)
}

export const getVideo = async (videoId: string): Promise<Video> => {
  const vid = await callApi({ endpoint: Endpoints.video, id: videoId })
  return transformDate(vid)
}

export const listVideos = async (
  queryParams: QueryParams,
): Promise<Video[]> => {
  const res = await callApi({
    endpoint: Endpoints.video,
    queryParams: buildQueryParams(queryParams),
  })
  return transformDates(res.data)
}

export const createVideo = async (video: Video) => {
  const vid = await callApi({
    endpoint: Endpoints.video,
    method: Method.POST,
    body: video,
  })
  return transformDate(vid)
}

export const deleteVideo = async (video: Video) => {
  return callApi({
    endpoint: Endpoints.video,
    method: Method.DELETE,
    id: video.id,
  })
}

export const updateVideo = async (video: Video) => {
  const vid = await callApi({
    endpoint: Endpoints.video,
    method: Method.PUT,
    id: video.id,
    body: video,
  })
  return transformDate(vid)
}

export const generateVideo = async (video: Video) => {
  return callApi({
    endpoint: Endpoints.video,
    method: Method.POST,
    id: video.id,
    secondPathParam: SecondPathParam.generate,
  })
}

export const createCreator = async (creator: Creator) => {
  return callApi({
    endpoint: Endpoints.creator,
    method: Method.POST,
    body: creator,
  })
}

export const getCreator = async (creatorId: string) => {
  return callApi({ endpoint: Endpoints.creator, id: creatorId })
}

export const updateCreator = async (creator: Creator) => {
  return callApi({
    endpoint: Endpoints.creator,
    method: Method.PATCH,
    id: creator.uid,
    body: creator,
  })
}

export const checkCheckoutResult = async (session_id: string) => {
  return callApi({
    endpoint: `${Endpoints.billing}/${SecondPathParam.checkout_sessions}`,
    id: session_id,
  })
}

export const checkCurrentUsage = async (
  creatorId: string,
): Promise<UsageDetails> => {
  return callApi({
    endpoint: Endpoints.creator,
    id: creatorId,
    secondPathParam: SecondPathParam.usage,
  })
}

export const getStripePortalUrl = async (
  creatorId: string,
  returnUrl: string,
) => {
  return callApi({
    endpoint: Endpoints.creator,
    id: creatorId,
    secondPathParam: SecondPathParam.stripe_portal_url,
    queryParams: { return_url: returnUrl },
  })
}

export const createStripeSession = async (creatorId: string) => {
  return callApi({
    endpoint: Endpoints.creator,
    id: creatorId,
    secondPathParam: SecondPathParam.stripe_session,
  })
}

export const listAgents = async (
  queryParams: QueryParams,
): Promise<Agent[]> => {
  const res = await callApi({
    endpoint: Endpoints.agent,
    queryParams: buildQueryParams(queryParams),
  })
  return transformDates(res.data)
}

export const listPublicAgents = async (): Promise<Agent[]> => {
  const agents = await callApi({
    endpoint: "demo/agents",
  })
  return transformDates(agents)
}

const normalizeAgentCapabilities = (agent: Agent): Agent => {
  const capabilities = agent?.capabilities as unknown
  if (!capabilities || Array.isArray(capabilities)) return agent
  if (typeof capabilities !== "object") return agent

  const normalized = Object.entries(
    capabilities as Record<string, { triggers?: string[] }>,
  ).map(([type, value]) => ({
    type: type as AgentCapabilityType,
    ...(value?.triggers ? { triggers: value.triggers } : {}),
  }))
  return { ...agent, capabilities: normalized }
}

export const getAgent = async (id: string): Promise<Agent> => {
  const agent = (await callApi({ endpoint: Endpoints.agent, id })) as Agent
  return normalizeAgentCapabilities(agent)
}

export const createAgent = async (agent: Agent) => {
  return callApi({
    endpoint: Endpoints.agent,
    method: Method.POST,
    body: agent,
  })
}

export const updateAgent = async (agent: Agent) => {
  return callApi({
    endpoint: Endpoints.agent,
    method: Method.PUT,
    id: agent.id,
    body: agent,
  })
}

export const deleteAgent = async (agentId: string) => {
  return callApi({
    endpoint: Endpoints.agent,
    method: Method.DELETE,
    id: agentId,
  })
}

export const generateAgent = async (agentId: string) => {
  return callApi({
    endpoint: Endpoints.agent,
    method: Method.POST,
    id: agentId,
    secondPathParam: SecondPathParam.generate,
  })
}

export const listKnowledge = async (queryParams: QueryParams) => {
  const res = await callApi({
    endpoint: Endpoints.knowledge,
    queryParams: buildQueryParams(queryParams),
  })
  return transformDates(res.data)
}

export const getKnowledge = async (knowledgeId: string) => {
  return callApi({ endpoint: Endpoints.knowledge, id: knowledgeId })
}

export const createKnowledge = async (knowledge: Knowledge) => {
  return callApi({
    endpoint: Endpoints.knowledge,
    method: Method.POST,
    body: knowledge,
  })
}

export const deleteKnowledge = async (knowledgeId: string) => {
  return callApi({
    endpoint: Endpoints.knowledge,
    method: Method.DELETE,
    id: knowledgeId,
  })
}

export const uploadKnowledge = async (knowledgeId: string, file: File) => {
  const idToken = await firebaseAuth.currentUser?.getIdToken()
  const chunk_size = 1024 * 1024 * 10 // Chunk size set to 10 MB
  let offset = 0
  let chunk_number = 0
  const total_chunks = Math.ceil(file.size / chunk_size)

  while (offset < file.size) {
    const chunk = file.slice(offset, offset + chunk_size)
    const chunk_blob = new Blob([chunk], { type: file.type })

    // Create a FormData object to send chunk data
    const formData = new FormData()
    formData.append("file", chunk_blob, file.name)
    formData.append("chunk_number", String(chunk_number))
    formData.append("total_chunks", String(total_chunks))

    const response = await fetch(
      `${backendUrlBaseUrl}/${DEFAULT_API_VERSION}/${Endpoints.knowledge}/${knowledgeId}/${SecondPathParam.upload}`,
      {
        method: Method.POST,
        body: formData,
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      },
    )

    if (response.status !== 200) {
      const json = await response.json()
      throw new Error(`${response.status}: ${(json as any).detail}`)
    }

    offset += chunk_size
    chunk_number += 1
  }

  return { message: "File uploaded successfully" }
}

export const processKnowledge = async (knowledgeId: string) => {
  return callApi({
    endpoint: Endpoints.knowledge,
    method: Method.POST,
    id: knowledgeId,
    secondPathParam: SecondPathParam.process,
  })
}

export const listAgentConversations = async (
  agentId: string,
): Promise<Conversation[]> => {
  const conversations = await callApi({
    endpoint: Endpoints.agent,
    id: agentId,
    secondPathParam: SecondPathParam.calls,
  })
  return transformDates(conversations)
}

export const listConversations = async (
  agentIds: string[],
): Promise<Conversation[]> => {
  const results = await Promise.all(agentIds.map(listAgentConversations))
  return results
    .flat()
    .sort(
      (a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0),
    )
}

export const deleteConversation = async (conversationId: string) => {
  return callApi({
    endpoint: Endpoints.calls,
    method: Method.DELETE,
    id: conversationId,
  })
}

export const listMessages = async (
  conversationId: string,
): Promise<Message[]> => {
  const messages = await callApi({
    endpoint: Endpoints.calls,
    id: conversationId,
    secondPathParam: SecondPathParam.messages,
  })
  const transformedMessages = messages.map((msg) => ({
    ...msg,
    sentAt: msg.sentAt ? new Date(msg.sentAt) : null,
  }))

  return transformedMessages.sort(
    (a, b) => (a.sentAt?.getTime() || 0) - (b.sentAt?.getTime() || 0),
  )
}

export const createFeedback = async (feedback: Partial<Feedback>) => {
  return callApi({
    endpoint: Endpoints.feedback,
    method: Method.POST,
    body: feedback,
  })
}

export const getFeedbacks = async (
  queryParams: QueryParams,
): Promise<Feedback[]> => {
  const res = await callApi({
    endpoint: Endpoints.feedback,
    queryParams: buildQueryParams(queryParams),
  })
  return res.data
}

export const getStripeProducts = async (): Promise<StripeProducts> => {
  return callApi({
    endpoint: `${Endpoints.billing}/${SecondPathParam.products}`,
  })
}

export const createCustomerCheckout = async (
  priceId: string,
  returnUrl: string,
  successUrl: string | null,
  promoCode: string | null,
): Promise<CustomerCheckoutResponse> => {
  const checkoutModel: CustomerCheckoutModel = {
    price_id: priceId,
    return_url: returnUrl,
    after_completion_url: successUrl,
    promo_code: promoCode,
  }

  return callApi({
    endpoint: `${Endpoints.billing}/${SecondPathParam.checkout_sessions}`,
    method: Method.POST,
    body: checkoutModel,
  })
}

export const togglePayAsYouGo = async (
  enabled: boolean,
  returnUrl: string,
): Promise<PayAsYouGoResponse> => {
  const payAsYouGoModel: PayAsYouGoModel = {
    enabled,
    return_url: returnUrl,
  }

  return callApi({
    endpoint: `${Endpoints.billing}/${SecondPathParam.pay_as_you_go}`,
    method: Method.POST,
    body: payAsYouGoModel,
  })
}

export const getDeveloperKeys = async (
  queryParams: QueryParams,
): Promise<DeveloperKey[]> => {
  const res = await callApi({
    endpoint: Endpoints.developer_key,
    queryParams: buildQueryParams(queryParams),
  })
  return res.data
}

export const createDeveloperKey = async (
  name: string,
): Promise<DeveloperKeyWithSecret> => {
  return callApi({
    endpoint: Endpoints.developer_key,
    method: Method.POST,
    body: { name },
  })
}

export const deleteDeveloperKey = async (keyId: string) => {
  return callApi({
    endpoint: Endpoints.developer_key,
    method: Method.DELETE,
    id: keyId,
  })
}

export const getCallOverview = async (
  callId: string,
): Promise<CallOverview> => {
  return callApi({
    endpoint: Endpoints.calls,
    id: callId,
    secondPathParam: SecondPathParam.overview,
  })
}

export const getCallInsights = async (
  callId: string,
): Promise<CallInsights> => {
  return callApi({
    endpoint: Endpoints.calls,
    id: callId,
    secondPathParam: SecondPathParam.insights,
  })
}

export const getCallNextSteps = async (callId: string) => {
  return callApi({
    endpoint: Endpoints.calls,
    id: callId,
    secondPathParam: SecondPathParam.next_steps,
  })
}

export type AskAiAboutCallPayload = {
  call_ids: string[]
  chat_request: {
    message: string
    system_prompt?: string
    participant_names?: { [key: string]: string }
    conversation_history: Message[]
  }
}

export const askAiAboutCall = async (
  payload: AskAiAboutCallPayload,
): Promise<{ response: string }> => {
  return callApi({
    endpoint: Endpoints.ask_ai_calls,
    method: Method.POST,
    body: payload,
  })
}

export const getWebhookConfig = async (): Promise<WebhookConfig | null> => {
  try {
    const config = await callApi({ endpoint: Endpoints.webhook })
    return transformDate(config)
  } catch (error: any) {
    if (error instanceof ApiError && error.status === 404) {
      return null
    }
    console.error("Failed to get webhook config:", error)
    throw error
  }
}

export const createWebhookConfig = async (
  config: Partial<WebhookConfig>,
): Promise<WebhookConfig> => {
  const payload = {
    url: config.url,
    enabled: config.enabled,
  }
  const webhookConfig = await callApi({
    endpoint: Endpoints.webhook,
    method: Method.POST,
    body: payload,
  })
  return transformDate(webhookConfig)
}

export const updateWebhookConfig = async (
  config: WebhookConfig,
): Promise<WebhookConfig> => {
  const payload = {
    url: config.url,
    enabled: config.enabled,
  }
  const webhookConfig = await callApi({
    endpoint: Endpoints.webhook,
    method: Method.PUT,
    body: payload,
  })
  return transformDate(webhookConfig)
}

export const deleteWebhookConfig = async (): Promise<{ success: boolean }> => {
  return callApi({ endpoint: Endpoints.webhook, method: Method.DELETE })
}

export const getAnalytics = async (monthOffset: number): Promise<Analytics> => {
  if (monthOffset > 0) {
    throw new Error("Month offset must be less than or equal to 0")
  }
  return callApi({
    endpoint: `${Endpoints.analytics}/${SecondPathParam.daily_usage}`,
    queryParams: { month_offset: monthOffset },
  })
}

export const createExternalApiConfiguration = async (
  configuration: ExternalApiConfigurationPayload,
): Promise<ExternalApiConfiguration> => {
  return callApi({
    endpoint: Endpoints.external_api,
    method: Method.POST,
    body: configuration,
  })
}

export const listExternalApiConfigurations = async (
  queryParams: QueryParams,
): Promise<ExternalApiConfigurationResponse> => {
  const res = await callApi({
    endpoint: Endpoints.external_api,
    queryParams: buildQueryParams(queryParams),
  })
  return res.data
}
